import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Order, OrderStatus } from '../../../types/order';
import { TodoTask } from '../../../types/todo';
import { Recipe } from '../../../types/recipe';

interface BatchRequirement {
  fillingName: string;
  batches: number;
  color: string; // Color based on oldest order's age
  orderDate: string; // Date of oldest order for sorting
}

interface OrderRequirement {
  fillingName: string;
  totalUnits: number;
  color: string; // Color based on oldest order's age
  orderDate: string; // Date of oldest order for sorting
}

/**
 * Calculates days since order date
 * @param orderDate - ISO date string
 * @returns number of days
 */
const getDaysSinceOrder = (orderDate: string): number => {
  if (!orderDate) return 0;

  const [year, month, day] = orderDate.split('-').map(Number);
  const orderDateTime = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - orderDateTime.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

/**
 * Gets color based on order age
 * @param days - number of days since order
 * @param status - order status
 * @returns hex color string
 */
const getOrderAgeColor = (days: number, status: OrderStatus): string => {
  // Completed orders get gray
  if (status === 'Completed') {
    return '#9ca3af'; // Gray
  }

  // Age-based colors
  if (days <= 1) return '#10b981'; // Green (Fresh - 0-1 days)
  if (days <= 3) return '#84cc16'; // Yellow-Green (Recent - 2-3 days)
  if (days <= 5) return '#eab308'; // Yellow (Warning - 4-5 days)
  if (days === 6) return '#f97316'; // Orange (Urgent - 6 days)
  return '#ef4444'; // Red (Overdue - 7+ days)
};

const DayPlanner: React.FC = () => {
  const orders = useSelector((state: RootState) => state.orders.orders);
  const fillings = useSelector((state: RootState) => state.recipes.fillings);
  const tasks = useSelector((state: RootState) => state.todo.tasks);
  const [isFillingPrepExpanded, setIsFillingPrepExpanded] = useState(false);
  const [isOrderFulfillmentExpanded, setIsOrderFulfillmentExpanded] = useState(false);

  // Selection state (separate for each section)
  const [selectedFillingPrepItems, setSelectedFillingPrepItems] = useState<Set<string>>(new Set());
  const [selectedOrderFulfillmentItems, setSelectedOrderFulfillmentItems] = useState<Set<string>>(new Set());

  // Shift-drag state for range selection
  const [isShiftDraggingPrep, setIsShiftDraggingPrep] = useState(false);
  const [prepDragStartIndex, setPrepDragStartIndex] = useState<number | null>(null);
  const [isShiftDraggingFulfillment, setIsShiftDraggingFulfillment] = useState(false);
  const [fulfillmentDragStartIndex, setFulfillmentDragStartIndex] = useState<number | null>(null);

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // Helper: Create custom drag image for multi-select
  const createMultiItemDragImage = (count: number): HTMLElement => {
    const dragImage = document.createElement('div');
    dragImage.style.cssText = `
      position: absolute;
      top: -1000px;
      left: -1000px;
      padding: 12px 20px;
      background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
      color: white;
      border-radius: 8px;
      font-weight: bold;
      font-size: 16px;
      box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
      white-space: nowrap;
      pointer-events: none;
      z-index: 9999;
    `;
    dragImage.textContent = `${count} items`;
    document.body.appendChild(dragImage);
    return dragImage;
  };

  const handleDragStart = (e: React.DragEvent<HTMLLIElement>, req: BatchRequirement) => {
    // Check if dragging a selected item or single item
    const isDraggingSelected = selectedFillingPrepItems.has(req.fillingName);
    const itemsToDrag = isDraggingSelected
      ? unscheduledBatchRequirements.filter(item => selectedFillingPrepItems.has(item.fillingName))
      : [req];

    const dragData = {
      items: itemsToDrag.map(item => ({
        fillingName: item.fillingName,
        batches: item.batches
      })),
      source: 'dayPlanner',
      isMultiSelect: itemsToDrag.length > 1
    };

    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'move';

    // Set custom drag image for multi-select
    if (itemsToDrag.length > 1) {
      const dragImage = createMultiItemDragImage(itemsToDrag.length);
      e.dataTransfer.setDragImage(dragImage, 0, 0);
      setTimeout(() => dragImage.remove(), 0);
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLLIElement>) => {
    // Reset any visual feedback
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleOrderDragStart = (e: React.DragEvent<HTMLLIElement>, req: OrderRequirement) => {
    // Check if dragging a selected item or single item
    const isDraggingSelected = selectedOrderFulfillmentItems.has(req.fillingName);
    const itemsToDrag = isDraggingSelected
      ? productionRequirements.filter(item => selectedOrderFulfillmentItems.has(item.fillingName))
      : [req];

    const dragData = {
      items: itemsToDrag.map(item => ({
        fillingName: item.fillingName,
        units: item.totalUnits
      })),
      source: 'dayPlannerOrderFulfillment',
      isMultiSelect: itemsToDrag.length > 1
    };

    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'move';

    // Set custom drag image for multi-select
    if (itemsToDrag.length > 1) {
      const dragImage = createMultiItemDragImage(itemsToDrag.length);
      e.dataTransfer.setDragImage(dragImage, 0, 0);
      setTimeout(() => dragImage.remove(), 0);
    }
  };

  const handleOrderDragEnd = (e: React.DragEvent<HTMLLIElement>) => {
    // Reset any visual feedback
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
  };

  // Event handler: Document-level mouse up for shift-drag
  React.useEffect(() => {
    const handleDocumentMouseUp = () => {
      setIsShiftDraggingPrep(false);
      setPrepDragStartIndex(null);
      setIsShiftDraggingFulfillment(false);
      setFulfillmentDragStartIndex(null);
    };

    document.addEventListener('mouseup', handleDocumentMouseUp);
    return () => document.removeEventListener('mouseup', handleDocumentMouseUp);
  }, []);

  // Event handler: Shift-click selection for Filling Prep
  const handleFillingPrepClick = (e: React.MouseEvent, fillingName: string) => {
    if (e.shiftKey) {
      // Clear other section and toggle this item
      setSelectedOrderFulfillmentItems(new Set());
      setSelectedFillingPrepItems(prev => {
        const newSet = new Set(prev);
        if (newSet.has(fillingName)) {
          newSet.delete(fillingName);
        } else {
          newSet.add(fillingName);
        }
        return newSet;
      });
    } else if (selectedFillingPrepItems.has(fillingName)) {
      // Regular click on selected item: deselect
      setSelectedFillingPrepItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(fillingName);
        return newSet;
      });
    } else {
      // Regular click on non-selected item: select only this item, clear others
      setSelectedOrderFulfillmentItems(new Set());
      setSelectedFillingPrepItems(new Set([fillingName]));
    }
  };

  // Event handler: Shift-click selection for Order Fulfillment
  const handleOrderFulfillmentClick = (e: React.MouseEvent, fillingName: string) => {
    if (e.shiftKey) {
      // Clear other section and toggle this item
      setSelectedFillingPrepItems(new Set());
      setSelectedOrderFulfillmentItems(prev => {
        const newSet = new Set(prev);
        if (newSet.has(fillingName)) {
          newSet.delete(fillingName);
        } else {
          newSet.add(fillingName);
        }
        return newSet;
      });
    } else if (selectedOrderFulfillmentItems.has(fillingName)) {
      // Regular click on selected item: deselect
      setSelectedOrderFulfillmentItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(fillingName);
        return newSet;
      });
    } else {
      // Regular click on non-selected item: select only this item, clear others
      setSelectedFillingPrepItems(new Set());
      setSelectedOrderFulfillmentItems(new Set([fillingName]));
    }
  };

  // Event handler: Shift-drag range selection for Filling Prep
  const handleFillingPrepMouseDown = (e: React.MouseEvent, index: number) => {
    if (e.shiftKey) {
      setIsShiftDraggingPrep(true);
      setPrepDragStartIndex(index);
      setSelectedOrderFulfillmentItems(new Set()); // Clear other section
    }
  };

  const handleFillingPrepMouseEnter = (index: number) => {
    if (isShiftDraggingPrep && prepDragStartIndex !== null) {
      const start = Math.min(prepDragStartIndex, index);
      const end = Math.max(prepDragStartIndex, index);

      const newSelection = new Set<string>();
      for (let i = start; i <= end; i++) {
        newSelection.add(unscheduledBatchRequirements[i].fillingName);
      }
      setSelectedFillingPrepItems(newSelection);
    }
  };

  // Event handler: Shift-drag range selection for Order Fulfillment
  const handleOrderFulfillmentMouseDown = (e: React.MouseEvent, index: number) => {
    if (e.shiftKey) {
      setIsShiftDraggingFulfillment(true);
      setFulfillmentDragStartIndex(index);
      setSelectedFillingPrepItems(new Set()); // Clear other section
    }
  };

  const handleOrderFulfillmentMouseEnter = (index: number) => {
    if (isShiftDraggingFulfillment && fulfillmentDragStartIndex !== null) {
      const start = Math.min(fulfillmentDragStartIndex, index);
      const end = Math.max(fulfillmentDragStartIndex, index);

      const newSelection = new Set<string>();
      for (let i = start; i <= end; i++) {
        newSelection.add(productionRequirements[i].fillingName);
      }
      setSelectedOrderFulfillmentItems(newSelection);
    }
  };

  // Event handler: Click outside to clear selections
  const handleContainerClick = (e: React.MouseEvent) => {
    // Only clear if clicking on the container itself (not on child elements)
    if (e.target === e.currentTarget) {
      setSelectedFillingPrepItems(new Set());
      setSelectedOrderFulfillmentItems(new Set());
    }
  };

  // Helper: Get selection highlight styling
  const getSelectionStyle = (isSelected: boolean): React.CSSProperties => {
    if (!isSelected) return {};

    return {
      boxShadow: '0 0 0 4px rgba(33, 150, 243, 0.4), 0 4px 12px rgba(33, 150, 243, 0.3)',
      outline: '2px solid #2196F3',
      outlineOffset: '2px',
      backgroundColor: '#E3F2FD',
      transform: 'translateX(4px)',
      transition: 'all 0.2s ease-in-out'
    };
  };

  // Calculate batch requirements for all pending/in-progress orders
  const batchRequirements: BatchRequirement[] = useMemo(() => {
    // Build TWO maps: one for active/pending tasks, one for completed tasks
    const scheduledOrdersByFilling: { [fillingName: string]: Set<string> } = {};
    const fulfilledOrdersByFilling: { [fillingName: string]: Set<string> } = {};

    tasks.forEach((task: TodoTask) => {
      // Only process batch tasks with the necessary fields
      if (task.taskType === 'batch' && task.fillingName) {
        const fillingName = task.fillingName;
        const orderIds = task.scheduledOrderIds || [];

        if (task.status === 'completed') {
          // Add to fulfilled orders map
          if (!fulfilledOrdersByFilling[fillingName]) {
            fulfilledOrdersByFilling[fillingName] = new Set();
          }
          orderIds.forEach(orderId => {
            fulfilledOrdersByFilling[fillingName].add(orderId);
          });
        } else {
          // Add to scheduled orders map (pending or in-progress)
          if (!scheduledOrdersByFilling[fillingName]) {
            scheduledOrdersByFilling[fillingName] = new Set();
          }
          orderIds.forEach(orderId => {
            scheduledOrdersByFilling[fillingName].add(orderId);
          });
        }
      }
    });

    // Aggregate batches by recipeId, excluding orders scheduled for each specific filling
    // IMPORTANT: Calculate batches per order FIRST, then sum them to avoid losing ceiling precision
    const batchesByRecipeId: { [recipeId: string]: { name: string; totalBatches: number } } = {};

    // Track which orders contribute to each filling
    const ordersByRecipeId: { [recipeId: string]: Order[] } = {};

    orders.forEach((order: Order) => {
      // Only process finalized orders that are not completed and not fully ready
      if (!order.isFinalized || order.status === 'Completed' || order.isReady) return;

      order.items.forEach(item => {
        // Check if this order is already scheduled OR fulfilled for this specific filling
        const scheduledOrders = scheduledOrdersByFilling[item.recipeName];
        const fulfilledOrders = fulfilledOrdersByFilling[item.recipeName];
        const isScheduled =
          (scheduledOrders && scheduledOrders.has(order.id)) || (fulfilledOrders && fulfilledOrders.has(order.id));

        // Only include unscheduled orders for this specific filling
        if (!isScheduled) {
          // Calculate how much of this item is already allocated (has inventory)
          const allocated = order.allocatedInventory?.find(alloc => alloc.productName === item.recipeName);
          const allocatedQty = allocated?.quantity || 0;

          // Calculate shortfall (amount still needed for production)
          const shortfall = item.quantity - allocatedQty;

          // Only calculate batches if there's a shortfall
          if (shortfall > 0) {
            // Find the recipe to calculate batches
            const filling = fillings.find((f: Recipe) => f.id === item.recipeId);
            if (filling && filling.yield > 0) {
              // Calculate batches for THIS order item's shortfall (ceiling ensures we round up)
              const batchesForThisItem = Math.ceil(shortfall / filling.yield);

              if (!batchesByRecipeId[item.recipeId]) {
                batchesByRecipeId[item.recipeId] = {
                  name: item.recipeName,
                  totalBatches: 0
                };
              }
              // Sum the batches (not quantities!)
              batchesByRecipeId[item.recipeId].totalBatches += batchesForThisItem;

              // Track which orders require this filling
              if (!ordersByRecipeId[item.recipeId]) {
                ordersByRecipeId[item.recipeId] = [];
              }
              // Avoid adding the same order multiple times
              if (!ordersByRecipeId[item.recipeId].some(o => o.id === order.id)) {
                ordersByRecipeId[item.recipeId].push(order);
              }
            }
          }
        }
      });
    });

    // Calculate batches for each filling
    const requirements: BatchRequirement[] = [];

    Object.entries(batchesByRecipeId).forEach(([recipeId, data]) => {
      const batches = data.totalBatches;

      // Find the oldest order requiring this filling
      const contributingOrders = ordersByRecipeId[recipeId] || [];
      let maxDays = -1;
      let maxDaysOrder: Order | undefined;

      contributingOrders.forEach(order => {
        const days = getDaysSinceOrder(order.orderDate);
        if (days > maxDays) {
          maxDays = days;
          maxDaysOrder = order;
        }
      });

      // Get color based on oldest order's age
      const color = maxDaysOrder
        ? getOrderAgeColor(getDaysSinceOrder(maxDaysOrder.orderDate), maxDaysOrder.status)
        : '#007bff'; // Default blue if no orders found

      requirements.push({
        fillingName: data.name,
        batches,
        color,
        orderDate: maxDaysOrder?.orderDate || '9999-12-31' // Use far future date if no order found
      });
    });

    // Sort by order date (oldest first)
    return requirements.sort((a, b) => a.orderDate.localeCompare(b.orderDate));
  }, [orders, fillings, tasks]);

  // Since batchRequirements now excludes scheduled orders, we can use it directly
  // Only filter out items with 0 batches (edge case)
  const unscheduledBatchRequirements = useMemo(() => {
    return batchRequirements.filter(req => req.batches > 0);
  }, [batchRequirements]);

  // Calculate production requirements for unfulfilled orders
  const productionRequirements: OrderRequirement[] = useMemo(() => {
    // Build TWO maps: one for active/pending tasks, one for completed tasks
    const scheduledOrdersByFilling: { [fillingName: string]: Set<string> } = {};
    const fulfilledOrdersByFilling: { [fillingName: string]: Set<string> } = {};

    tasks.forEach((task: TodoTask) => {
      // Only process fulfillment tasks with the necessary fields
      if (task.taskType === 'fulfillment' && task.fillingName) {
        const fillingName = task.fillingName;
        const orderIds = task.scheduledOrderIds || [];

        if (task.status === 'completed') {
          // Add to fulfilled orders map
          if (!fulfilledOrdersByFilling[fillingName]) {
            fulfilledOrdersByFilling[fillingName] = new Set();
          }
          orderIds.forEach(orderId => {
            fulfilledOrdersByFilling[fillingName].add(orderId);
          });
        } else {
          // Add to scheduled orders map (pending or in-progress)
          if (!scheduledOrdersByFilling[fillingName]) {
            scheduledOrdersByFilling[fillingName] = new Set();
          }
          orderIds.forEach(orderId => {
            scheduledOrdersByFilling[fillingName].add(orderId);
          });
        }
      }
    });

    // Filter for unfulfilled orders (not Completed and not fully ready)
    const unfulfilledOrders = orders.filter(
      (order: Order) => order.isFinalized && order.status !== 'Completed' && !order.isReady
    );

    // Group by filling type and sum quantities, excluding scheduled orders
    const requirementsByFilling: { [fillingName: string]: number } = {};

    // Track which orders contribute to each filling
    const ordersByFilling: { [fillingName: string]: Order[] } = {};

    unfulfilledOrders.forEach((order: Order) => {
      order.items.forEach(item => {
        // Check if this order is already scheduled OR fulfilled for this filling
        const scheduledOrders = scheduledOrdersByFilling[item.recipeName];
        const fulfilledOrders = fulfilledOrdersByFilling[item.recipeName];
        const isScheduled =
          (scheduledOrders && scheduledOrders.has(order.id)) || (fulfilledOrders && fulfilledOrders.has(order.id));

        // Only include unscheduled orders
        if (!isScheduled) {
          // Calculate how much of this item is already allocated (has inventory)
          const allocated = order.allocatedInventory?.find(alloc => alloc.productName === item.recipeName);
          const allocatedQty = allocated?.quantity || 0;

          // Calculate shortfall (amount still needed for production)
          const shortfall = item.quantity - allocatedQty;

          // Only add to Day Planner if there's a shortfall
          if (shortfall > 0) {
            if (!requirementsByFilling[item.recipeName]) {
              requirementsByFilling[item.recipeName] = 0;
            }
            requirementsByFilling[item.recipeName] += shortfall;

            // Track which orders require this filling
            if (!ordersByFilling[item.recipeName]) {
              ordersByFilling[item.recipeName] = [];
            }
            // Avoid adding the same order multiple times
            if (!ordersByFilling[item.recipeName].some(o => o.id === order.id)) {
              ordersByFilling[item.recipeName].push(order);
            }
          }
        }
      });
    });

    // Convert to array with color information and sort by filling name
    return Object.entries(requirementsByFilling)
      .map(([fillingName, totalUnits]) => {
        // Find the oldest order requiring this filling
        const contributingOrders = ordersByFilling[fillingName] || [];
        let maxDays = -1;
        let maxDaysOrder: Order | undefined;

        contributingOrders.forEach(order => {
          const days = getDaysSinceOrder(order.orderDate);
          if (days > maxDays) {
            maxDays = days;
            maxDaysOrder = order;
          }
        });

        // Get color based on oldest order's age
        const color = maxDaysOrder
          ? getOrderAgeColor(getDaysSinceOrder(maxDaysOrder.orderDate), maxDaysOrder.status)
          : '#007bff'; // Default blue if no orders found

        return {
          fillingName,
          totalUnits,
          color,
          orderDate: maxDaysOrder?.orderDate || '9999-12-31' // Use far future date if no order found
        };
      })
      .sort((a, b) => a.orderDate.localeCompare(b.orderDate));
  }, [orders, tasks]);

  return (
    <div style={styles.container} onClick={handleContainerClick}>
      <div style={styles.header}>
        <h2 style={styles.title}>Day Planner</h2>
        <span style={styles.date}>{today}</span>
      </div>

      <div style={styles.content} onClick={handleContainerClick}>
        {batchRequirements.length > 0 || productionRequirements.length > 0 ? (
          <div style={styles.batchList}>
            <div style={styles.subsectionHeader} onClick={() => setIsFillingPrepExpanded(!isFillingPrepExpanded)}>
              <h3 style={styles.subsectionTitle}>Filling Prep</h3>
              <span style={styles.expandIcon}>{isFillingPrepExpanded ? '▼' : '▶'}</span>
            </div>
            {isFillingPrepExpanded && (
              <>
                {unscheduledBatchRequirements.length > 0 ? (
                  <ul style={styles.list}>
                    {unscheduledBatchRequirements.map((req, index) => {
                      const isSelected = selectedFillingPrepItems.has(req.fillingName);
                      return (
                        <li
                          key={index}
                          draggable={true}
                          onDragStart={e => handleDragStart(e, req)}
                          onDragEnd={handleDragEnd}
                          onClick={e => handleFillingPrepClick(e, req.fillingName)}
                          onMouseDown={e => handleFillingPrepMouseDown(e, index)}
                          onMouseEnter={() => handleFillingPrepMouseEnter(index)}
                          style={{
                            ...styles.listItem,
                            borderLeft: `4px solid ${req.color}`,
                            cursor: 'grab',
                            userSelect: 'none',
                            ...getSelectionStyle(isSelected)
                          }}
                        >
                          <span style={styles.fillingName}>{req.fillingName}:</span>{' '}
                          <span style={styles.batchCount}>{req.batches} batches</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p style={styles.scheduledMessage}>All filling prep items have been scheduled!</p>
                )}
              </>
            )}

            {/* Order Fulfillment Section */}
            <div
              style={styles.subsectionHeader}
              onClick={() => setIsOrderFulfillmentExpanded(!isOrderFulfillmentExpanded)}
            >
              <h3 style={styles.subsectionTitle}>Order Fulfillment</h3>
              <span style={styles.expandIcon}>{isOrderFulfillmentExpanded ? '▼' : '▶'}</span>
            </div>
            {isOrderFulfillmentExpanded && (
              <div style={styles.placeholderContent}>
                {productionRequirements.length > 0 ? (
                  <ul style={styles.list}>
                    {productionRequirements.map((req, index) => {
                      const isSelected = selectedOrderFulfillmentItems.has(req.fillingName);
                      return (
                        <li
                          key={index}
                          draggable={true}
                          onDragStart={e => handleOrderDragStart(e, req)}
                          onDragEnd={handleOrderDragEnd}
                          onClick={e => handleOrderFulfillmentClick(e, req.fillingName)}
                          onMouseDown={e => handleOrderFulfillmentMouseDown(e, index)}
                          onMouseEnter={() => handleOrderFulfillmentMouseEnter(index)}
                          style={{
                            ...styles.listItem,
                            borderLeft: `4px solid ${req.color}`,
                            cursor: 'grab',
                            userSelect: 'none',
                            ...getSelectionStyle(isSelected)
                          }}
                        >
                          <span style={styles.fillingName}>{req.fillingName}:</span>{' '}
                          <span style={styles.batchCount}>{req.totalUnits} units needed</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p style={styles.placeholder}>No unfulfilled orders</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <p style={styles.placeholder}>No orders scheduled for today</p>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '2px solid #f0f0f0',
    backgroundColor: '#fafafa',
    flexWrap: 'wrap',
    gap: '12px'
  },
  title: {
    margin: 0,
    fontSize: 'clamp(20px, 5vw, 24px)',
    fontWeight: '700',
    color: '#333'
  },
  date: {
    fontSize: 'clamp(13px, 3vw, 14px)',
    color: '#666',
    fontWeight: '500'
  },
  content: {
    padding: '20px',
    flex: 1,
    overflow: 'auto'
  },
  placeholder: {
    margin: 0,
    fontSize: '15px',
    color: '#999',
    fontStyle: 'italic',
    textAlign: 'center'
  },
  scheduledMessage: {
    margin: '16px 0',
    padding: '12px',
    fontSize: '14px',
    color: '#10b981',
    fontStyle: 'italic',
    textAlign: 'center',
    backgroundColor: '#f0fdf4',
    borderRadius: '6px'
  },
  batchList: {
    width: '100%'
  },
  subsectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f0f4f8',
    borderRadius: '6px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    userSelect: 'none'
  },
  subsectionTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#444'
  },
  expandIcon: {
    fontSize: '12px',
    color: '#666',
    transition: 'transform 0.2s'
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  listItem: {
    padding: '12px 16px',
    marginBottom: '8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    fontSize: '15px',
    transition: 'background-color 0.2s'
  },
  fillingName: {
    fontWeight: '600',
    color: '#333'
  },
  batchCount: {
    color: '#007bff',
    fontWeight: '700'
  },
  placeholderContent: {
    padding: '16px 0'
  },
  ordersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  orderCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '12px',
    transition: 'box-shadow 0.2s'
  },
  orderHeader: {
    marginBottom: '8px'
  },
  orderInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px'
  },
  customerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  readyBadge: {
    fontSize: '11px',
    padding: '3px 6px',
    backgroundColor: '#4CAF50',
    color: 'white',
    borderRadius: '3px',
    fontWeight: 'bold'
  },
  orderMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#666'
  },
  orderDate: {
    fontSize: '13px',
    color: '#666'
  },
  ageBadge: {
    fontSize: '11px',
    padding: '3px 6px',
    borderRadius: '3px',
    color: 'white',
    fontWeight: 'bold'
  },
  orderDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #f0f0f0'
  },
  itemCount: {
    fontSize: '13px',
    color: '#666'
  },
  orderStatus: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#2196F3'
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingTop: '8px',
    borderTop: '1px solid #f0f0f0'
  },
  orderItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    padding: '4px 8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px'
  },
  itemName: {
    color: '#333',
    fontWeight: '500'
  },
  itemQuantity: {
    color: '#007bff',
    fontWeight: '600'
  }
};

export default DayPlanner;
